import type { Dispatch, FunctionComponent, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import type {
  DatasourceAdf,
  JiraIssuesDatasourceAdf,
} from '@atlaskit/link-datasource';
import { Analytics } from '@trello/atlassian-analytics';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { useFeatureGate } from '@trello/feature-gate-client';
import { intl } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import {
  Spotlight,
  SpotlightManager,
  SpotlightTarget,
  SpotlightTransition,
} from '@trello/nachos/onboarding-experimental';
import type { PopoverProps } from '@trello/nachos/popover';
import type { PIIString } from '@trello/privacy';
import { convertToPIIString } from '@trello/privacy';
import type { InlineCardAdf } from '@trello/smart-card';
import { useGlobalTheme } from '@trello/theme';

import { useSelectNewUserOnboardingCampaign } from 'app/src/components/NewUserOnboardingCampaign/useSelectNewUserOnboardingCampaign';
import {
  LazySmartListJiraIssueModal,
  ListSourcesPopover,
} from 'app/src/components/SmartList';
import {
  closeCreateJiraListModal,
  useIsCreateJiraListModalOpen,
} from 'app/src/components/SmartList/jiraIssueModalState';
import { splitScreenOnboardingCampaign } from 'app/src/components/SplitScreenOnboardingCampaign';
import { useCreateSmartListButtonSpotlightQuery } from './CreateSmartListButtonSpotlightQuery.generated';

import styles from './CreateSmartListButton.less';

const CREATE_SMART_LIST_SPOTLIGHT_MESSAGE = 'create-smart-list-spotlight';

export interface CreateSmartListButtonProps {
  saveList: (list: {
    name: string;
    datasourceLink: PIIString;
    datasourceFilter: boolean;
    type: 'datasource';
  }) => Promise<void>;
  hide(): void;
  triggerRef: (node: HTMLButtonElement | null) => void;
  popoverProps: PopoverProps<HTMLButtonElement>;
  toggle(): void;
  setIsSpotlightOpen: Dispatch<SetStateAction<boolean>>;
}

export const CreateSmartListButton: FunctionComponent<
  CreateSmartListButtonProps
> = ({
  saveList,
  hide,
  toggle,
  triggerRef,
  popoverProps,
  setIsSpotlightOpen,
}: CreateSmartListButtonProps) => {
  const [hasInteractedWithSpotlight, setHasInteractedWithSpotlight] =
    useState(false);

  const { data, loading } = useCreateSmartListButtonSpotlightQuery();
  const member = useMemo(() => {
    if (loading) {
      return null;
    }
    return data?.member;
  }, [loading, data?.member]);
  const newUserOnboardingCampaign = useSelectNewUserOnboardingCampaign(
    member?.campaigns ?? [],
  );
  const isInNewUserOnboardingExperiment =
    newUserOnboardingCampaign?.name === splitScreenOnboardingCampaign.name;

  const { value: isBlockingSpotlightEnabled } = useFeatureGate(
    'trello_block_smart_list_spotlight',
  );

  const { effectiveColorMode } = useGlobalTheme();
  const spotlightBGColor = effectiveColorMode === 'light' ? '#FFFFFF' : '';

  const showJiraModal = useIsCreateJiraListModalOpen();

  const { dismissOneTimeMessage, isOneTimeMessageDismissed } =
    useOneTimeMessagesDismissed();

  const isSpotlightDismissed = isOneTimeMessageDismissed(
    CREATE_SMART_LIST_SPOTLIGHT_MESSAGE,
  );

  const openListSourcesPopover = useCallback(() => {
    setHasInteractedWithSpotlight(true);
    dismissOneTimeMessage(CREATE_SMART_LIST_SPOTLIGHT_MESSAGE);
    toggle();
  }, [dismissOneTimeMessage, toggle]);

  const onClickTryIt = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'trySmartListsButton',
      source: 'createSmartListSpotlight',
    });
    openListSourcesPopover();
  }, [openListSourcesPopover]);

  const onClickSpotlightTarget = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'addSmartListFromDatasourceButton',
      source: 'createSmartListSpotlight',
    });
    openListSourcesPopover();
  }, [openListSourcesPopover]);

  const onClickAddFrom = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'addSmartListFromDatasourceButton',
      source: 'inlineListComposerInlineDialog',
    });
    openListSourcesPopover();
  }, [openListSourcesPopover]);

  const onInsert = useCallback(
    async (adf: DatasourceAdf<Record<string, unknown>> | InlineCardAdf) => {
      closeCreateJiraListModal();
      const JiraIssuesAdf = adf as JiraIssuesDatasourceAdf;
      if (
        JiraIssuesAdf?.attrs?.datasource?.parameters &&
        JiraIssuesAdf.attrs.url
      ) {
        const datasourceUrl = JiraIssuesAdf.attrs.url;
        await saveList({
          name: intl.formatMessage({
            id: 'templates.smart_lists.jira-list',
            defaultMessage: 'Jira list',
            description: 'Default name for new Jira lists',
          }),
          datasourceLink: convertToPIIString(datasourceUrl),
          datasourceFilter: false,
          type: 'datasource',
        });
      }
    },
    [saveList],
  );

  const onClose = useCallback(() => {
    closeCreateJiraListModal();
  }, []);

  const dismiss = useCallback(() => {
    setHasInteractedWithSpotlight(true);
    dismissOneTimeMessage(CREATE_SMART_LIST_SPOTLIGHT_MESSAGE);
    Analytics.sendDismissedComponentEvent({
      componentType: 'spotlight',
      componentName: 'createSmartListSpotlight',
      source: 'inlineListComposerInlineDialog',
    });
  }, [dismissOneTimeMessage]);

  const canShowSpotlight =
    !hasInteractedWithSpotlight &&
    !showJiraModal &&
    !isSpotlightDismissed &&
    !(isBlockingSpotlightEnabled && isInNewUserOnboardingExperiment);

  useEffect(() => {
    // Informs List Composer's click-outside behavior
    setIsSpotlightOpen(canShowSpotlight);
  }, [canShowSpotlight, setIsSpotlightOpen]);

  useEffect(() => {
    // Keeps spotlight from opening after modal is closed
    // Relevant for "Add list from Jira issues" edit list action
    if (showJiraModal) {
      setHasInteractedWithSpotlight(true);
    }
  }, [showJiraModal, setIsSpotlightOpen]);

  useEffect(() => {
    if (canShowSpotlight) {
      Analytics.sendViewedComponentEvent({
        componentName: 'createSmartListSpotlight',
        componentType: 'spotlight',
        source: 'inlineListComposerInlineDialog',
      });
    }
  }, [canShowSpotlight]);

  return (
    <>
      <SpotlightManager>
        <SpotlightTarget name="create-smart-list-spotlight">
          <Button
            ref={triggerRef}
            appearance="subtle"
            className={styles.createSmartListButton}
            iconAfter={<ChevronDownIcon label="" />}
            onClick={onClickAddFrom}
            type="button"
            aria-expanded={popoverProps.isVisible}
            aria-label={intl.formatMessage({
              id: 'templates.smart_lists.list-from-jira-work-items',
              defaultMessage: 'Add list from Jira work items',
              description:
                'Button to create a List from other product data sources',
            })}
          >
            <FormattedMessage
              id="templates.smart_lists.add-from"
              defaultMessage="Add from"
              description="Button to create a List from other product data sources"
            />
          </Button>
        </SpotlightTarget>
        <SpotlightTransition>
          {canShowSpotlight && (
            <Spotlight
              heading={intl.formatMessage({
                id: 'templates.smart_lists.track-jira-progress-2',
                defaultMessage: 'Track Jira progress in Trello',
                description:
                  'Spotlight dialog header for List Composer Add From button',
              })}
              actions={[
                {
                  onClick: onClickTryIt,
                  text: intl.formatMessage({
                    id: 'templates.smart_lists.spotlight-try-it',
                    defaultMessage: 'Try it',
                    description:
                      'Spotlight dialog button for trying out the new feature',
                  }),
                },
                {
                  onClick: dismiss,
                  appearance: 'subtle',
                  text: intl.formatMessage({
                    id: 'templates.smart_lists.spotlight-dismiss',
                    defaultMessage: 'Dismiss',
                    description: 'Spotlight dialog dismiss button',
                  }),
                },
              ]}
              dialogPlacement="bottom center"
              dialogWidth={296}
              targetRadius={4}
              target="create-smart-list-spotlight"
              key="create-smart-list-spotlight"
              targetBgColor={spotlightBGColor}
              targetOnClick={onClickSpotlightTarget}
            >
              <FormattedMessage
                id="templates.smart_lists.stay-organized"
                defaultMessage="Add a Jira list to track and manage work without leaving Trello to stay organized effortlessly."
                description="Spotlight dialog messaging for List Composer Add From button"
              />
            </Spotlight>
          )}
        </SpotlightTransition>
      </SpotlightManager>
      <ListSourcesPopover hide={hide} popoverProps={popoverProps} />
      {showJiraModal && (
        <LazySmartListJiraIssueModal onClose={onClose} onInsert={onInsert} />
      )}
    </>
  );
};
